import { useCallback, useRef } from 'react';
import { formatPrice } from '../../utils/formatPrice';

/**
 * A4 Invoice print component.
 * Renders an inline preview with a print button that uses window.print().
 */
export default function InvoicePrint({ order, shopInfo, onClose, settings }) {
  const printRef = useRef(null);

  const handlePrint = useCallback(() => {
    const content = printRef.current;
    if (!content) return;

    // Create an iframe for isolated printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
<title>Invoice #${order.number}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1e293b; padding: 40px; font-size: 14px; line-height: 1.6; }
  @media print { body { padding: 20px; } @page { margin: 15mm; } }
</style>
</head>
<body>${content.innerHTML}</body>
</html>`);
    doc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    // Clean up after print dialog closes
    const cleanup = () => {
      document.body.removeChild(iframe);
    };
    iframe.contentWindow.addEventListener('afterprint', cleanup);
    // Fallback cleanup after 60s in case afterprint doesn't fire
    setTimeout(cleanup, 60000);
  }, [order]);

  if (!order) return null;

  const shop = shopInfo || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Preview header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Invoice Preview</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-primary-dark transition-colors"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Invoice preview (also used as print source) */}
        <div ref={printRef} className="p-8" style={{ fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: '#1e293b', fontSize: '14px', lineHeight: '1.6' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
            <div>
              {settings?.invoice_logo_url && (
                <img src={settings.invoice_logo_url} alt="" style={{ display: 'block', maxHeight: '60px', marginBottom: '12px' }} />
              )}
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{shop.name || 'Shop'}</div>
              {(shop.address || shop.city) && (
                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>
                  {[shop.address, shop.address_2, shop.city, shop.state, shop.postcode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#3FBF9F' }}>INVOICE</div>
              <div style={{ color: '#64748b', fontSize: '13px', marginTop: '8px' }}>
                <div><strong style={{ color: '#1e293b' }}>#{order.number}</strong></div>
                <div>{formatDate(order.date_created)}</div>
              </div>
            </div>
          </div>

          {/* Customer */}
          {order.customer && order.customer.name && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '8px' }}>Bill To</div>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                <p style={{ fontWeight: 600, marginBottom: '2px' }}>{order.customer.name}</p>
                {order.customer.email && <p style={{ marginBottom: '2px' }}>{order.customer.email}</p>}
                {order.customer.phone && <p>{order.customer.phone}</p>}
              </div>
            </div>
          )}

          {/* Items table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>Item</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, i) => (
                <tr key={item.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px' }}>
                    {item.name}
                    {item.sku && <div style={{ color: '#94a3b8', fontSize: '12px' }}>{item.sku}</div>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{item.quantity}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatPrice(item.price)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatPrice(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ marginLeft: 'auto', width: '280px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <span>Subtotal</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            {order.discount_total > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span>Discount</span>
                <span>-{formatPrice(order.discount_total)}</span>
              </div>
            )}
            {order.total_tax > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span>Tax</span>
                <span>{formatPrice(order.total_tax)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', marginTop: '8px', borderTop: '2px solid #1e293b', fontSize: '18px', fontWeight: 700 }}>
              <span>Total</span>
              <span>{formatPrice(order.total)}</span>
            </div>
          </div>

          {/* Payment method */}
          <div style={{ marginTop: '24px' }}>
            <span style={{ display: 'inline-block', background: '#f0fdf4', color: '#166534', padding: '12px 16px', borderRadius: '8px', fontSize: '14px' }}>
              Paid via <strong>{order.payment_title || order.payment_method}</strong>
            </span>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
            <p>Thank you for your purchase!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
